<template>
	<CSideMenu :active="showMenu" width="s" placement="right" class="account-menu" @close="handleClose">
		<CAccountMenuContainer
			:size="size"
			@close-menu="handleClose"
		/>
		<COrgMenu
			:orgs="internalOrgs"
			:current-org="org"
			:is-admin="isGlobalAdmin"
			:org-access-promises="orgAccessPromises"
			@close-side-menus="handleClose"
			@check-org-access="handleOrgAccess"
		/>
	</CSideMenu>
</template>

<script lang="ts">
	import { defineComponent, ref, watch, type PropType } from 'vue';
	import CSideMenu from './SideMenu.vue';
	import COrgMenu from './OrgMenu.vue';
	import CAccountMenuContainer from './AccountMenuContainer.vue';
	import { canAccessOrganization } from '@cognitoforms/api/services/organization-service';
	import type { UserOrganizationMeta } from '@cognitoforms/types/server-types/core/services/user-organization-meta';
	import type { OrganizationMeta } from '@cognitoforms/types/server-types/core/services/organization-meta';
	import type { AvatarSize } from 'src/components/auth/Avatar.vue';

	export type OrganizationAccessMeta = UserOrganizationMeta & {
		Disabled: boolean,
		FirstOwnerEmail?: string,
		FirstOwnerName?: string,
		IsBillingRestricted?: boolean
	}

	export default defineComponent({
		name: 'CAccountAndOrgFlyout',
		components: {
			CSideMenu,
			CAccountMenuContainer,
			COrgMenu
		},
		props: {
			showMenu: {
				type: Boolean,
				required: true,
				default: false
			},
			orgs: {
				type: Array as PropType<UserOrganizationMeta[]>,
				required: true
			},
			org: {
				type: Object as PropType<OrganizationMeta>,
				required: true
			},
			size: {
				type: String as PropType<AvatarSize>,
				default: 's'
			},
			isGlobalAdmin: {
				type: Boolean,
				default: false,
				required: true
			}
		},
		emits: ['close-side-menu'],
		setup(props, { emit }) {
			const internalOrgs = ref<OrganizationAccessMeta[]>(props.orgs.map((o: UserOrganizationMeta) => {
				return {
					...o,
					Disabled: false
				};
			}));

			watch(() => props.orgs, () => {
				internalOrgs.value = props.orgs.map((o: UserOrganizationMeta) => {
					return {
						...o,
						Disabled: false
					};
				});
			});

			let orgsAccessChecked = new Set<string>();
			const orgAccessPromises = ref<Map<string, Promise<any>>>(new Map<string, Promise<any>>());
			const handleClose = () => {
				orgsAccessChecked = new Set<string>();
				orgAccessPromises.value = new Map<string, Promise<any>>();
				emit('close-side-menu');
			};

			const handleOrgAccess = (orgId: string) => {
				if (!orgsAccessChecked.has(orgId)) {
					orgsAccessChecked.add(orgId);
					orgAccessPromises.value[orgId] = canAccessOrganization(orgId).then(() => {})
						.catch((e) => {
							const org = internalOrgs.value.find(o => o.Id === orgId);
							org.Disabled = true;
							org.FirstOwnerEmail = e.data.Email;
							org.FirstOwnerName = e.data.FullName;
							org.IsBillingRestricted = e.data.IsBillingRestricted;
						});
				}
			};

			return {
				handleClose,
				orgAccessPromises,
				handleOrgAccess,
				internalOrgs
			};
		}
	});
</script>